/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, Fragment, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FeedbackReplyState } from "types/models/feedback"
import { EventAction, EventCategory } from "constants/tracking"
import { TroyWebTracker } from "utils/tracking"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { getGrabXValueFromLocalStorage, MerchantPortalFeatureFlag } from "grabx"
import { ReviewUnReportableReason } from "types/api/menuFeedback.type"
import { FeedbackDrawer } from "./FeedbackDrawer"
import { FeedbackReportDrawer } from "./FeedbackReportDrawer"
import FeedbackDrawerPopup from "./FeedbackDrawerPopup"
import FeedbackReportThrottle from "./FeedbackReportThrottle"

export const FeedbackDrawerContainer: FC = () => {
  const dispatch = useDispatch()

  const {
    feedback: { replyState },
    feedbackDrawer: {
      showReply,
      showReport,
      review,
      actionType,
      reviewThrottle,
      isFetchingReviewThrottle,
    },
  } = useSelector((state: AppState) => state)
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false)

  const enableMerchantReviewThrottle = getGrabXValueFromLocalStorage<boolean>(
    MerchantPortalFeatureFlag.enableMerchantReviewThrottle,
  )
  const showMerchantReviewThrottle =
    showReport &&
    review &&
    enableMerchantReviewThrottle &&
    reviewThrottle &&
    !isFetchingReviewThrottle &&
    reviewThrottle.reviewUnreportableReason !==
      ReviewUnReportableReason.ReviewUnreportableReasonReportable

  const onCancelDrawer = () => {
    dispatch(actionCreator.feedbackDrawer.toggleReply({ visible: false }))
    if (replyState === FeedbackReplyState.TOUCHED) {
      setShowCloseConfirmation(true)
    } else {
      setShowCloseConfirmation(false)
    }
  }

  const handleUnsavedConfirmation = (cont: boolean) => {
    if (!cont) {
      dispatch(actionCreator.feedback.ackUnsaveReply())

      TroyWebTracker.sendEvent({
        category: EventCategory.feedbackReplyEdit,
        action: EventAction.clickCancel,
        labels: {
          REVIEW_ID: review?.reviewID,
          REVIEW_RATING: review?.rating,
        },
      })
    }
    dispatch(actionCreator.feedbackDrawer.toggleReply({ visible: false }))
    setShowCloseConfirmation(false)
  }

  return (
    <Fragment>
      {showReply && review && actionType && (
        <FeedbackDrawer
          data-testid="feedback-drawer"
          visible={showReply}
          onClose={onCancelDrawer}
          actionType={actionType}
          review={review}
        />
      )}
      {showMerchantReviewThrottle ? (
        <FeedbackReportThrottle
          data-testid="feedback-throttle-drawer"
          visible={showReport}
          reviewThrottle={reviewThrottle}
          onClose={() => {
            dispatch(
              actionCreator.feedbackDrawer.toggleReport({ visible: false }),
            )
          }}
        />
      ) : (
        showReport &&
        review &&
        !isFetchingReviewThrottle && (
          <FeedbackReportDrawer
            data-testid="feedback-report-drawer"
            review={review}
            visible={showReport}
            onClose={() => {
              dispatch(
                actionCreator.feedbackDrawer.toggleReport({ visible: false }),
              )
            }}
          />
        )
      )}
      <FeedbackDrawerPopup
        data-testid="feedback-drawer-popup"
        visible={showCloseConfirmation}
        onCancel={() => handleUnsavedConfirmation(false)}
        onConfirm={() => handleUnsavedConfirmation(true)}
      />
    </Fragment>
  )
}
